/**
 * Rule-based tuning hints from in-app knowledge (Basic Stats interpretation).
 * Desirable bands come from Advanced settings (merged defaults in jackInternalDefaults.json).
 */

/** @typedef {'ok' | 'warn' | 'neutral'} TuningStatus */

function inClosedRange(v, lo, hi) {
  return Number.isFinite(v) && Number.isFinite(lo) && Number.isFinite(hi) && v >= lo && v <= hi;
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {object} summarySnapshot — from SuspensionAnalysisPanel summarySnapshot
 * @param {object|null} stF — jack stats.front
 * @param {object|null} stR — jack stats.rear
 * @param {number} frontTravelMm
 * @param {number} rearTravelMm
 * @param {object} ranges — getTuningGuideConfig()
 */
export function analyzeTuningGuide(summarySnapshot, stF, stR, frontTravelMm, rearTravelMm, ranges) {
  const r = ranges || {};

  const rebMin = num(r.tuneReboundP95Min) ?? 1000;
  const rebMax = num(r.tuneReboundP95Max) ?? 1400;
  const compMin = num(r.tuneCompressionP95Min) ?? 2500;
  const compMax = num(r.tuneCompressionP95Max) ?? 4000;
  const sagMin = num(r.tuneDynamicSagPctMin) ?? 25;
  const sagMax = num(r.tuneDynamicSagPctMax) ?? 35;
  const balMin = num(r.tuneBalanceRatioMin) ?? 0.75;
  const balMax = num(r.tuneBalanceRatioMax) ?? 1.15;
  const maxBoTotal = num(r.tuneMaxBottomOutsTotal) ?? 3;
  const maxBoEnd = num(r.tuneMaxBottomOutsPerEnd) ?? 2;
  const packGood = num(r.tunePackingIndexGoodMax) ?? 30;
  const minTravelUse = num(r.tuneMinTravelUsePct) ?? 70;
  const roughWarn = num(r.tuneRoughnessWarnAbove) ?? 0;

  const summary = {
    balance: /** @type {TuningStatus} */ ('neutral'),
    sagFront: /** @type {TuningStatus} */ ('neutral'),
    sagRear: /** @type {TuningStatus} */ ('neutral'),
    travelFront: /** @type {TuningStatus} */ ('neutral'),
    travelRear: /** @type {TuningStatus} */ ('neutral'),
    bottomFront: /** @type {TuningStatus} */ ('neutral'),
    bottomRear: /** @type {TuningStatus} */ ('neutral'),
    bottomTotal: /** @type {TuningStatus} */ ('neutral'),
    topBalance: /** @type {TuningStatus} */ ('neutral'),
    packing: /** @type {TuningStatus} */ ('neutral'),
    roughness: /** @type {TuningStatus} */ ('neutral'),
  };

  const br = num(summarySnapshot?.balanceRatio);
  if (br != null) {
    summary.balance = inClosedRange(br, balMin, balMax) ? 'ok' : 'warn';
  }

  const sf = num(summarySnapshot?.dynSagFrontPct);
  const sr = num(summarySnapshot?.dynSagRearPct);
  if (sf != null) summary.sagFront = inClosedRange(sf, sagMin, sagMax) ? 'ok' : 'warn';
  if (sr != null) summary.sagRear = inClosedRange(sr, sagMin, sagMax) ? 'ok' : 'warn';

  const tf = num(summarySnapshot?.travelFrontPct);
  const tr = num(summarySnapshot?.travelRearPct);
  const bF = num(summarySnapshot?.bottomF);
  const bR = num(summarySnapshot?.bottomR);
  if (tf != null) {
    const lowUse = tf < minTravelUse && (bF === 0 || bF == null);
    const ok = tf >= minTravelUse || (bF != null && bF > 0);
    summary.travelFront = lowUse ? 'warn' : ok ? 'ok' : 'neutral';
  }
  if (tr != null) {
    const lowUse = tr < minTravelUse && (bR === 0 || bR == null);
    const ok = tr >= minTravelUse || (bR != null && bR > 0);
    summary.travelRear = lowUse ? 'warn' : ok ? 'ok' : 'neutral';
  }

  if (bF != null) summary.bottomFront = bF <= maxBoEnd ? 'ok' : 'warn';
  if (bR != null) summary.bottomRear = bR <= maxBoEnd ? 'ok' : 'warn';
  const boSum = (bF != null ? bF : 0) + (bR != null ? bR : 0);
  if (bF != null || bR != null) summary.bottomTotal = boSum <= maxBoTotal ? 'ok' : 'warn';

  const tF = num(summarySnapshot?.topF);
  const tR = num(summarySnapshot?.topR);
  if (tF != null && tR != null && tR >= tF) summary.topBalance = 'warn';
  else if (tF != null && tR != null) summary.topBalance = 'ok';

  const pi = num(summarySnapshot?.packingIndex);
  if (summarySnapshot?.jackOk && pi != null) {
    summary.packing = pi <= packGood ? 'ok' : 'warn';
  }

  const rough = num(summarySnapshot?.roughness);
  if (roughWarn > 0 && rough != null) {
    summary.roughness = rough <= roughWarn ? 'ok' : 'warn';
  }

  const { kbSlices, kbBullets } = buildKbRecommendations({
    summarySnapshot,
    stF,
    stR,
    summary,
    rebMin,
    rebMax,
    compMin,
    compMax,
    maxBoTotal,
    packGood,
    balMin,
    balMax,
  });

  const jackFrontRows = stF ? jackTableRowStatuses('front', stF, frontTravelMm, ranges) : {};
  const jackRearRows = stR ? jackTableRowStatuses('rear', stR, rearTravelMm, ranges) : {};

  return { summary, kbBullets, kbSlices, jackFrontRows, jackRearRows };
}

/** @typedef {{ observation: string, recommendation: string }} KbRecItem */

function emptySideSlices() {
  return {
    compression: /** @type {KbRecItem[]} */ ([]),
    rebound: /** @type {KbRecItem[]} */ ([]),
    other: /** @type {KbRecItem[]} */ ([]),
  };
}

function emptyKbSlices() {
  return {
    general: /** @type {KbRecItem[]} */ ([]),
    front: emptySideSlices(),
    rear: emptySideSlices(),
  };
}

/** @param {KbRecItem} item */
function flattenItem(item) {
  const r = (item.recommendation || '').trim();
  if (!r) return item.observation.trim();
  return `${item.observation.trim()} — ${r}`;
}

function flattenKbBullets(slices) {
  const out = [];
  for (const it of slices.general) out.push(flattenItem(it));
  for (const side of ['front', 'rear']) {
    const s = slices[side];
    for (const it of [...s.compression, ...s.rebound, ...s.other]) out.push(flattenItem(it));
  }
  return out;
}

/**
 * @returns {{ kbSlices: ReturnType<typeof emptyKbSlices>, kbBullets: string[] }}
 */
function buildKbRecommendations(ctx) {
  const {
    summarySnapshot,
    stF,
    stR,
    summary,
    rebMin,
    rebMax,
    compMin,
    compMax,
    maxBoTotal,
    packGood,
    balMin,
    balMax,
  } = ctx;

  const slices = emptyKbSlices();
  /** @param {'general'|'front'|'rear'} side @param {'compression'|'rebound'|'other'} bucket @param {KbRecItem} item */
  const push = (side, bucket, item) => {
    if (side === 'general') slices.general.push(item);
    else slices[side][bucket].push(item);
  };

  const bF = num(summarySnapshot?.bottomF);
  const bR = num(summarySnapshot?.bottomR);
  const boSum = (bF != null ? bF : 0) + (bR != null ? bR : 0);
  if (boSum > maxBoTotal) {
    push('general', 'other', {
      observation: `Bottom-outs (F+R): ${boSum} total exceeds your limit (${maxBoTotal}).`,
      recommendation: 'Increase spring support or progression; address repeated hard bottoming.',
    });
  }

  const tf = num(summarySnapshot?.travelFrontPct);
  const tr = num(summarySnapshot?.travelRearPct);
  if (tf != null && tr != null && tf < 60 && tr < 60 && boSum === 0) {
    push('general', 'other', {
      observation: 'Both ends show low travel use with no bottom-outs.',
      recommendation: 'Spring may be stiff or sag high—try softer spring or lower sag so travel can work.',
    });
  }

  const checkSideVel = (sideKey, label, st) => {
    if (!st?.velocity?.compression || !st?.velocity?.rebound) return;
    const cp = num(st.velocity.compression.p95);
    const rp = num(st.velocity.rebound.p95);
    if (cp != null && rp != null && rp >= cp) {
      push(sideKey, 'rebound', {
        observation: `${label}: rebound p95 (${rp.toFixed(0)} mm/s) ≥ compression p95 (${cp.toFixed(0)} mm/s).`,
        recommendation: 'Rebound may be fast vs compression—try more rebound damping (LSR/HSR); confirm on trail.',
      });
    }
    if (rp != null && (rp < rebMin || rp > rebMax)) {
      push(sideKey, 'rebound', {
        observation: `${label}: rebound p95 ${rp.toFixed(0)} mm/s is outside ${rebMin}–${rebMax} mm/s (your reference band).`,
        recommendation: 'Adjust HSR toward that band; treat as a hint—cross-check with feel.',
      });
    }
    if (cp != null && (cp < compMin || cp > compMax)) {
      push(sideKey, 'compression', {
        observation: `${label}: compression p95 ${cp.toFixed(0)} mm/s is outside ${compMin}–${compMax} mm/s (broad reference).`,
        recommendation: 'Supporting signal only—trail-dependent; compare stroke depth and bottom-outs.',
      });
    }
  };
  checkSideVel('front', 'Front', stF);
  checkSideVel('rear', 'Rear', stR);

  const tF = num(summarySnapshot?.topF);
  const tR = num(summarySnapshot?.topR);
  if (tF != null && tR != null && tR >= tF) {
    push('general', 'other', {
      observation: 'Rear top-outs are not lower than front.',
      recommendation: 'Check rear spring vs front rebound balance, sag, and rebound velocities.',
    });
  }

  const pi = num(summarySnapshot?.packingIndex);
  if (summarySnapshot?.jackOk && pi != null && pi > packGood) {
    push('general', 'other', {
      observation: `Packing index ${pi} is above your good threshold (${packGood}).`,
      recommendation: 'Review rebound (too slow can pack) and spring balance; compare comp vs rebound stroke counts.',
    });
  }

  if (summary.balance === 'warn') {
    const br = num(summarySnapshot?.balanceRatio);
    push('general', 'other', {
      observation: `F/R mean travel ratio${br != null ? ` ≈ ${br.toFixed(2)}` : ''} is outside ${balMin}–${balMax}.`,
      recommendation: 'Review spring rates, sag, and whether one end is taking disproportionate load.',
    });
  }

  const strokeImbalance = (compC, rebC, sideKey, label) => {
    if (compC == null || rebC == null || compC < 8 || rebC < 8) return;
    const ratio = compC / rebC;
    if (ratio > 1.35) {
      push(sideKey, 'compression', {
        observation: `${label}: compression stroke count (${compC}) is much higher than rebound (${rebC}).`,
        recommendation: 'Can indicate packing or rebound too slow—revisit rebound and spring balance.',
      });
    }
  };
  if (stF?.velocity?.compression && stF?.velocity?.rebound) {
    strokeImbalance(stF.velocity.compression.count, stF.velocity.rebound.count, 'front', 'Front');
  }
  if (stR?.velocity?.compression && stR?.velocity?.rebound) {
    strokeImbalance(stR.velocity.compression.count, stR.velocity.rebound.count, 'rear', 'Rear');
  }

  const hasAny =
    slices.general.length +
      slices.front.compression.length +
      slices.front.rebound.length +
      slices.front.other.length +
      slices.rear.compression.length +
      slices.rear.rebound.length +
      slices.rear.other.length >
    0;

  if (!hasAny) {
    slices.general.push({
      observation: 'No major flags from this run against your configured ranges.',
      recommendation: 'Cross-check feel on trail and compare runs (A/B) when you change spring or damping.',
    });
  }

  return { kbSlices: slices, kbBullets: flattenKbBullets(slices) };
}

function jackTableRowStatuses(_side, stats, totalTravelMm, ranges) {
  const r = ranges || {};
  const rebMin = num(r.tuneReboundP95Min) ?? 1000;
  const rebMax = num(r.tuneReboundP95Max) ?? 1400;
  const compMin = num(r.tuneCompressionP95Min) ?? 2500;
  const compMax = num(r.tuneCompressionP95Max) ?? 4000;
  const sagMin = num(r.tuneDynamicSagPctMin) ?? 25;
  const sagMax = num(r.tuneDynamicSagPctMax) ?? 35;
  const maxBoEnd = num(r.tuneMaxBottomOutsPerEnd) ?? 2;

  const pos = stats.position;
  const comp = stats.velocity?.compression;
  const reb = stats.velocity?.rebound;
  const tt = num(totalTravelMm);
  const rows = {};

  if (!pos || !comp || !reb || !Number.isFinite(tt) || tt <= 0) return rows;

  const maxPct = (pos.max / tt) * 100;
  const sagPct = (pos.mean / tt) * 100;
  rows.maxTravelPct = maxPct >= 50 ? 'ok' : maxPct < 40 && (comp.bottomOutCount || 0) === 0 ? 'warn' : 'ok';
  rows.dynamicSagPct = inClosedRange(sagPct, sagMin, sagMax) ? 'ok' : 'warn';
  rows.compP95 = inClosedRange(comp.p95, compMin, compMax) ? 'ok' : 'neutral';
  const rebInBand = inClosedRange(reb.p95, rebMin, rebMax);
  const rebKick = Number.isFinite(reb.p95) && Number.isFinite(comp.p95) && reb.p95 >= comp.p95;
  rows.rebP95 = rebKick || !rebInBand ? 'warn' : 'ok';
  rows.bottomOuts = (comp.bottomOutCount || 0) <= maxBoEnd ? 'ok' : 'warn';

  const compC = comp.count;
  const rebC = reb.count;
  if (compC > 0 && rebC > 0) {
    const ratio = compC / rebC;
    rows.strokeBalance = ratio > 1.35 || ratio < 1 / 1.35 ? 'warn' : 'ok';
  } else {
    rows.strokeBalance = 'neutral';
  }

  return rows;
}

/** Map row keys used in JackBenchmarkStatsTable to status */
export function jackRowKeyForLabel(section, label) {
  if (section === 'Position') {
    if (label === 'Max Travel') return 'maxTravelPct';
    if (label === 'Dynamic Sag') return 'dynamicSagPct';
    return null;
  }
  if (section === 'Compression') {
    if (label === 'Stroke Count') return 'strokeBalance';
    if (label === '95th % Vel') return 'compP95';
    if (label === 'Bottom-outs') return 'bottomOuts';
    return null;
  }
  if (section === 'Rebound') {
    if (label === 'Stroke Count') return 'strokeBalance';
    if (label === '95th % Vel') return 'rebP95';
    return null;
  }
  return null;
}

export function buildOpenAiPayload(summarySnapshot, stF, stR, frontTravelMm, rearTravelMm, kbBullets) {
  return {
    summarySnapshot,
    frontTravelMm,
    rearTravelMm,
    jackFront: stF
      ? {
          position: stF.position,
          compression: stF.velocity?.compression,
          rebound: stF.velocity?.rebound,
        }
      : null,
    jackRear: stR
      ? {
          position: stR.position,
          compression: stR.velocity?.compression,
          rebound: stR.velocity?.rebound,
        }
      : null,
    ruleBasedHighlights: kbBullets,
  };
}
